import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { dbService } from './dbService';
import { UserProfile } from '../types';
import { isAdmin } from '../constants';

export const authService = {
  async login(initialRole?: UserProfile['role'], isStrict = false) {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    let profile = await dbService.getUser(user.uid) as UserProfile | null;
    const isAdminEmail = isAdmin(user.email);
    
    // If no profile found by Google UID, check if there's an existing profile by email
    if (!profile && user.email) {
      const existingProfileByEmail = await dbService.getUserByEmail(user.email);
      if (existingProfileByEmail) {
        const oldUid = existingProfileByEmail.uid;
        profile = {
          ...existingProfileByEmail,
          uid: user.uid,
          avatar: user.photoURL || existingProfileByEmail.avatar
        } as UserProfile;
        await dbService.createUser(profile);
        if (oldUid && oldUid !== user.uid) {
          await dbService.deleteUser(oldUid);
        }
      }
    }
    
    if (profile && profile.role !== 'customer' && !profile.isVerified && !isAdminEmail) {
      throw new Error('Akun Anda sedang menunggu verifikasi admin. Silakan tunggu beberapa saat.');
    }

    // Admin restriction
    if (initialRole === 'admin' && !isAdminEmail) {
      throw new Error('Hanya admin resmi yang dapat masuk ke konsol ini.');
    }

    // Barber verification
    if (initialRole === 'barber' && !isAdminEmail) {
      if (!profile || profile.role !== 'barber') {
        throw new Error('Akun Anda belum terverifikasi sebagai Kapster. Silakan daftar dan tunggu persetujuan admin.');
      }
    }

    if (!profile) {
      const isAdminEmail = isAdmin(user.email);
      const newProfile: UserProfile = {
        uid: user.uid,
        name: user.displayName || 'User',
        email: user.email || '',
        role: isAdminEmail ? 'admin' : (initialRole || 'customer'),
        isVerified: isAdminEmail ? true : false,
        avatar: user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`,
        notificationSettings: {
          newBooking: true,
          statusUpdate: true,
          newChat: true
        }
      };
      await dbService.createUser(newProfile);
      return newProfile;
    }
    return profile as UserProfile;
  },

  async register(registrationData: { name: string; email: string; phone: string; address: string }) {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    let profile = await dbService.getUser(user.uid) as UserProfile | null;
    if (profile) {
      if (!profile.isVerified) {
        throw new Error('Selamat Anda telah mendaftar. Tunggu verifikasi admin untuk mengaktifkan akun Anda.');
      }
      throw new Error('Akun dengan email ini sudah terdaftar. Silakan login.');
    }

    const newProfile: UserProfile = {
      uid: user.uid,
      name: registrationData.name,
      email: user.email || registrationData.email,
      phone: registrationData.phone,
      address: registrationData.address,
      role: 'customer',
      isVerified: true,
      avatar: user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.uid}`,
      notificationSettings: {
        newBooking: true,
        statusUpdate: true,
        newChat: true
      }
    };
    
    await dbService.createUser(newProfile);
    return newProfile;
  },

  async logout() {
    await signOut(auth);
  },

  onAuth(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  }
};
